import { useEffect, useRef, useState } from 'react'
import { Stack } from '../stack/stack'
import { CarouselButtonNext } from '../carousel-button-next/carousel-button-next'
import clsx from 'clsx'
import { WistiaPlayer, WistiaPlayerElement } from '@wistia/wistia-player-react'
import { isWistiaPlayerReady, playWistiaPlayer } from '~/utils/wistia'
import type { VideoSliderItemProps } from '~/routes/_content._product.ai/data/video-slider'

type VideoSliderProps = {
  videoSliderData: VideoSliderItemProps[]
}

export const VideoSlider = ({ videoSliderData }: VideoSliderProps) => {
  const [activeVideo, setActiveVideo] = useState(0)
  const [loadedVideoIndexes, setLoadedVideoIndexes] = useState<Set<number>>(
    () => new Set([0, 1].filter((index) => index < videoSliderData.length)),
  )
  const wistiaRefs = useRef<(WistiaPlayerElement | null)[]>([null])
  const videoCount = videoSliderData.length

  const showPreviousVideo = () => {
    setActiveVideo((prev) => (prev - 1 + videoCount) % videoCount)
  }

  const showNextVideo = () => {
    setActiveVideo((prev) => (prev + 1) % videoCount)
  }

  const [prevActiveVideo, setPrevActiveVideo] = useState(activeVideo)
  if (activeVideo !== prevActiveVideo) {
    setPrevActiveVideo(activeVideo)
    setLoadedVideoIndexes((currentIndexes) => {
      const nextIndexes = new Set(currentIndexes)
      nextIndexes.add(activeVideo)
      nextIndexes.add((activeVideo + 1) % videoCount)
      return nextIndexes
    })
  }

  useEffect(() => {
    wistiaRefs.current.forEach((player, index) => {
      if (index !== activeVideo && isWistiaPlayerReady(player)) {
        player.pause()
      }
    })

    const active = wistiaRefs.current[activeVideo]
    if (isWistiaPlayerReady(active)) playWistiaPlayer(active)
  }, [activeVideo, videoCount])

  return (
    <>
      <div className="video-slider__wrapper" aria-live="polite">
        <div className="video-slider__track">
          {videoSliderData.map((video, index) => {
            const isActive = index === activeVideo
            return (
              <div
                key={video.videoId}
                className={clsx('video', { 'is-active': isActive })}
                onClick={() => setActiveVideo(index)}
              >
                <div className="video-wrapper">
                  {loadedVideoIndexes.has(index) && (
                    <WistiaPlayer
                      transparentLetterbox
                      mediaId={video.videoId}
                      ref={(el) => {
                        wistiaRefs.current[index] = el
                      }}
                      autoplay={index === 0}
                      muted={true}
                    />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="video-slider-controls">
        <Stack className="video-controls" gap={4} row={true} justify="center">
          {videoSliderData.map((video, index) => (
            <button
              key={video.videoId}
              className={clsx('video-tab', {
                'is-active': index === activeVideo,
              })}
              onClick={() => setActiveVideo(index)}
              type="button"
            >
              {video.title}
            </button>
          ))}
        </Stack>

        <div className="carousel-controls-next">
          <CarouselButtonNext
            aria-label="Show previous AI workflow"
            direction="previous"
            onClick={showPreviousVideo}
          />
          <CarouselButtonNext
            aria-label="Show next AI workflow"
            direction="next"
            onClick={showNextVideo}
          />
        </div>
      </div>
    </>
  )
}
